import React, { useState, useRef, useEffect } from "react";

interface EditableTextProps {
  text: string;
  onEdit: (value: string) => void;
  className?: string;
  style?: React.CSSProperties;
  placeholder?: string;
}

const EditableText: React.FC<EditableTextProps> = ({
  text,
  onEdit,
  className = "",
  style = {},
  placeholder = "Click to edit",
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState(text);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setValue(text);
  }, [text]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleClick = () => {
    setIsEditing(true);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setValue(newValue);
    onEdit(newValue); // Save in real-time
  };

  const closeInput = (shouldRevert: boolean) => {
    setIsEditing(false);
    if (shouldRevert) {
      setValue(text);
      onEdit(text);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === "Escape") {
      e.preventDefault();
      closeInput(e.key === "Escape");
    }
  };

  const handleBlur = () => {
    closeInput(false);
  };

  if (isEditing) {
    const originalStyle = window.getComputedStyle(
      document.querySelector(`.${className.split(" ")[0]}`) || document.body,
    );
    const originalFontSize = parseFloat(originalStyle.fontSize);

    return (
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        className={`bg-transparent border-b-2 border-white/30 focus:border-white/70 outline-none px-2 py-1 w-full ${className}`}
        style={{
          ...style,
          fontSize: `${originalFontSize * 0.95}px`,
          caretColor: "white",
          cursor: "text",
          transition: "all 0.2s ease",
        }}
        placeholder={placeholder}
        onClick={(e) => e.stopPropagation()}
      />
    );
  }

  return (
    <div
      onClick={handleClick}
      className={`cursor-text ${className}`}
      style={{
        ...style,
        transition: "all 0.2s ease",
      }}
    >
      {text || <span className="opacity-50">{placeholder}</span>}
    </div>
  );
};

const defaultObjectives = [
  {
    goal: "Master Core Concepts",
    description: "Build a strong foundation in fundamental principles",
    icon: "🎯",
  },
  {
    goal: "Analyze & Evaluate",
    description: "Develop critical thinking and analytical skills",
    icon: "🔍",
  },
  {
    goal: "Practical Application",
    description: "Apply knowledge to real-world scenarios",
    icon: "🛠️",
  },
  {
    goal: "Problem Solving",
    description: "Learn effective strategies for complex challenges",
    icon: "💡",
  },
];

interface SlideContent {
  title?: string;
  subtitle?: string;
  objectives?: string;
  teacherNote?: string;
  bgColor?: string;
  onEdit?: (content: SlideContent) => void;
}

const LearningObjectivesSlide1 = {
  id: "learning-objectives1-slide",
  name: "Learning Objectives Slide (Modern Grid)",
  fields: [
    {
      id: "title",
      label: "Title",
      type: "text",
      defaultValue: "Today's Learning Journey",
    },
    {
      id: "subtitle",
      label: "Subtitle",
      type: "text",
      defaultValue: "By the end of this lesson, you will be able to...",
    },
    {
      id: "objectives",
      label: "Learning Objectives (JSON array of { goal, description, icon })",
      type: "text",
      defaultValue: JSON.stringify(defaultObjectives, null, 2),
    },
    {
      id: "teacherNote",
      label: "Teacher Note",
      type: "text",
      defaultValue: "",
    },
    {
      id: "bgColor",
      label: "Background Color",
      type: "text",
      defaultValue:
        "linear-gradient(135deg, #1e40af 0%, #1d4ed8 50%, #3b82f6 100%)",
    },
  ],
  render: (content: SlideContent) => {
    const {
      title = "Today's Learning Journey",
      subtitle = "By the end of this lesson, you will be able to...",
      teacherNote = "",
      bgColor = "linear-gradient(135deg, #1e40af 0%, #1d4ed8 50%, #3b82f6 100%)",
      objectives = JSON.stringify(defaultObjectives),
      onEdit = () => {},
    } = content;

    const handleEdit = (field: keyof SlideContent) => (value: string) => {
      const updatedContent = { ...content, [field]: value };
      onEdit(updatedContent);

      // Update the configuration in PreviewSlides
      if (window.updateLiveConfig) {
        window.updateLiveConfig(updatedContent);
      }
    };

    let parsedObjectives = defaultObjectives.slice(0, 8); // Limit to max 8 objectives
    try {
      if (typeof objectives === "string") {
        const parsed = JSON.parse(objectives);
        if (Array.isArray(parsed) && parsed.length > 0) {
          parsedObjectives = parsed.slice(0, 8); // Limit to max 8
        }
      } else if (Array.isArray(objectives) && objectives.length > 0) {
        parsedObjectives = objectives.slice(0, 8); // Limit to max 8
      }
    } catch (error) {
      console.warn("Error parsing objectives, using defaults:", error);
    }

    // Calculate layout based on number of objectives
    const getGridLayout = (count: number) => {
      if (count <= 2) return "grid-cols-2";
      if (count <= 4) return "grid-cols-2";
      if (count <= 6) return "grid-cols-3";
      return "grid-cols-4";
    };

    // Add text size calculation based on grid layout
    const getTextSizes = (count: number) => {
      if (count <= 2)
        return {
          goal: "text-3xl",
          description: "text-xl",
        };
      if (count <= 4)
        return {
          goal: "text-2xl",
          description: "text-lg",
        };
      if (count <= 6)
        return {
          goal: "text-xl",
          description: "text-base",
        };
      return {
        goal: "text-lg",
        description: "text-sm",
      };
    };

    const gridClass = getGridLayout(parsedObjectives.length);
    const textSizes = getTextSizes(parsedObjectives.length);

    return (
      <div
        className="w-full h-full relative overflow-hidden text-white"
        style={{ background: bgColor }}
      >
        {/* Animated Background Elements */}
        <div className="absolute inset-0">
          {[...Array(5)].map((_, i) => (
            <div
              key={i}
              className="absolute rounded-full mix-blend-overlay filter blur-xl opacity-50"
              style={{
                width: Math.random() * 400 + 200 + "px",
                height: Math.random() * 400 + 200 + "px",
                background: `rgba(255, 255, 255, ${Math.random() * 0.1})`,
                left: Math.random() * 100 + "%",
                top: Math.random() * 100 + "%",
                animation: `float-${i} ${20 + i * 5}s infinite linear`,
                transform: "translate(-50%, -50%)",
              }}
            />
          ))}
        </div>

        {/* Content Container - Modified to single column layout */}
        <div className="relative z-10 flex flex-col h-full p-8">
          {/* Header Section */}
          <div className="mb-12 text-center">
            <EditableText
              text={title}
              onEdit={handleEdit("title")}
              className="text-5xl font-black tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-white via-white to-white/70"
              placeholder="Enter title..."
            />
            <div className="mt-4">
              <EditableText
                text={subtitle}
                onEdit={handleEdit("subtitle")}
                className="text-2xl font-light text-white/90"
                placeholder="Enter subtitle..."
              />
            </div>
          </div>

          {/* Objectives Grid - Dynamic layout */}
          <div className={`grid ${gridClass} gap-6 flex-grow`}>
            {parsedObjectives.map((obj, idx) => (
              <div
                key={idx}
                className="group bg-white/10 backdrop-blur-sm rounded-lg border border-white/20 p-6 hover:bg-white/20 transition-all duration-300"
              >
                <div className="flex flex-col h-full">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="flex-shrink-0 w-14 h-14 bg-white/20 rounded-lg flex items-center justify-center text-2xl">
                      {obj.icon}
                    </div>
                    <div className="flex-shrink-0 w-8 h-8 bg-white/20 rounded-full flex items-center justify-center text-lg font-bold">
                      {idx + 1}
                    </div>
                  </div>
                  <div className="flex-grow space-y-3">
                    <EditableText
                      text={obj.goal}
                      onEdit={(value) => {
                        const newObjectives = [...parsedObjectives];
                        newObjectives[idx] = { ...obj, goal: value };
                        handleEdit("objectives")(JSON.stringify(newObjectives));
                      }}
                      className={`font-bold ${textSizes.goal} text-white/90 leading-tight`}
                      placeholder={`Enter objective ${idx + 1}...`}
                    />
                    <EditableText
                      text={obj.description}
                      onEdit={(value) => {
                        const newObjectives = [...parsedObjectives];
                        newObjectives[idx] = { ...obj, description: value };
                        handleEdit("objectives")(JSON.stringify(newObjectives));
                      }}
                      className={`${textSizes.description} text-white/80 leading-relaxed`}
                      placeholder={`Enter description ${idx + 1}...`}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Teacher Note - Only show if there's content */}
          {teacherNote && (
            <div className="relative mt-6 text-center">
              <div className="absolute -left-2 top-1/2 transform -translate-y-1/2 w-2 h-2 bg-yellow-400 rounded-full" />
              <EditableText
                text={teacherNote}
                onEdit={handleEdit("teacherNote")}
                className="text-base font-light italic text-white/90"
                placeholder="Enter teacher note..."
              />
            </div>
          )}
        </div>
      </div>
    );
  },
};

// Add required styles
const style = document.createElement("style");
style.textContent = `
  ${[...Array(5)]
    .map(
      (_, i) => `
    @keyframes float-${i} {
      0% { transform: translate(-50%, -50%) rotate(0deg); }
      100% { transform: translate(-50%, -50%) rotate(360deg); }
    }
  `,
    )
    .join("\n")}
`;
document.head.appendChild(style);

// Declare global window interface
declare global {
  interface Window {
    updateLiveConfig?: (config: SlideContent) => void;
  }
}

export default LearningObjectivesSlide1;
